import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import { positionLabelKey, type Position } from "../../access/positions";
import { ParityResponse } from "./types";

const ParityView = () => {
    const { t } = useTranslation();
    const [data, setData] = useState<ParityResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [positionFilter, setPositionFilter] = useState("");
    const [kindFilter, setKindFilter] = useState<"" | "reduction" | "widening">("");

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const res = await apiFetch("/access-control/parity");
                if (!res.ok) { if (alive) setError(t("server_error")); return; }
                const body: ParityResponse = await res.json();
                if (alive) setData(body);
            } catch {
                if (alive) setError(t("server_error"));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [t]);

    const positions = useMemo(() => {
        if (!data) return [];
        return Array.from(new Set(data.differences.map(d => d.position))).sort();
    }, [data]);

    const filtered = useMemo(() => {
        if (!data) return [];
        return data.differences.filter(d =>
            (!positionFilter || d.position === positionFilter)
            && (!kindFilter || d.kind === kindFilter));
    }, [data, positionFilter, kindFilter]);

    const reductions = data?.differences.filter(d => d.kind === "reduction").length ?? 0;
    const widenings = data?.differences.filter(d => d.kind === "widening").length ?? 0;

    return (
        <div className="ac-parity">
            {loading && <div className="ac-users-loading">{t("loading")}</div>}
            {error && <p className="ac-hint ac-hint--error">{error}</p>}
            {data && (
                <>
                    <div className="ac-parity-summary">
                        <div className="ac-parity-stat"><strong>{Array.isArray(data.warehouses) ? data.warehouses.length : data.warehouses}</strong><span>{t("ac_parity_warehouses")}</span></div>
                        <div className="ac-parity-stat"><strong>{data.cells}</strong><span>{t("ac_parity_cells")}</span></div>
                        <div className="ac-parity-stat"><strong>{data.differences.length}</strong><span>{t("ac_parity_differences")}</span></div>
                        <div className="ac-parity-stat ac-parity-stat--reduction"><strong>{reductions}</strong><span>{t("ac_parity_reductions")}</span></div>
                        <div className="ac-parity-stat ac-parity-stat--widening"><strong>{widenings}</strong><span>{t("ac_parity_widenings")}</span></div>
                        {data.migrated && (
                            <div className={`ac-parity-stat ${data.migrated.ok ? "ac-parity-stat--widening" : "ac-parity-stat--reduction"}`} data-testid="ac-parity-migrated">
                                <strong>{data.migrated.undecided}</strong>
                                <span>{t(data.migrated.ok ? "ac_parity_migrated_ok" : "ac_parity_migrated_undecided")}</span>
                            </div>
                        )}
                        {typeof data.newPositions === "number" && (
                            <div className="ac-parity-stat"><strong>{data.newPositions}</strong><span>{t("ac_parity_new_positions")}</span></div>
                        )}
                    </div>

                    <div className="ac-preview-controls">
                        <label className="ac-preview-field">
                            <span>{t("ac_preview_position")}</span>
                            <select value={positionFilter} onChange={e => setPositionFilter(e.target.value)}>
                                <option value="">{t("status_all")}</option>
                                {positions.map(p => (
                                    <option key={p} value={p}>{t(positionLabelKey(p as Position))}</option>
                                ))}
                            </select>
                        </label>
                        <label className="ac-preview-field">
                            <span>{t("ac_parity_kind")}</span>
                            <select value={kindFilter} onChange={e => setKindFilter(e.target.value as "" | "reduction" | "widening")}>
                                <option value="">{t("status_all")}</option>
                                <option value="reduction">{t("ac_parity_reductions")}</option>
                                <option value="widening">{t("ac_parity_widenings")}</option>
                            </select>
                        </label>
                    </div>

                    <div className="ac-matrix-scroll">
                        <table className="ac-matrix-table">
                            <thead>
                                <tr>
                                    <th>{t("warehouse")}</th>
                                    <th>{t("ac_preview_position")}</th>
                                    <th>{t("ac_key_col")}</th>
                                    <th>{t("ac_preview_legacy")}</th>
                                    <th>{t("ac_parity_rules_col")}</th>
                                    <th>{t("ac_parity_kind")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((d, i) => (
                                    <tr key={`${d.warehouse}-${d.position}-${d.key}-${i}`}>
                                        <td>{d.warehouse}</td>
                                        <td>{t(positionLabelKey(d.position as Position))}</td>
                                        <td><code className="ac-key-code">{d.key}</code></td>
                                        <td>{d.legacy}</td>
                                        <td>{d.rules}</td>
                                        <td>
                                            <span className={`ac-tag ac-tag--${d.kind}`}>
                                                {t(d.kind === "reduction" ? "ac_parity_reductions" : "ac_parity_widenings")}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={6} className="ac-hint">{t("ac_parity_none")}</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default ParityView;
